import { useState } from 'react'
import { Link } from 'react-router-dom'
import Modal from '@components/common/Modal'
import { QUEUE_PATH } from '@config/reportRoutes'
import { fmtDuration, fmtRows, fmtWhenInZone } from './queueFormat'
import { RUN_STATE_LABEL } from './scheduleFormat'
import { firmTextClass, unnamedReportLabel } from './jobIdentity'
import FirmText from './FirmText'

/**
 * "View run history" — the schedule's recent runs (design screen 6's kebab item).
 *
 * The data is already on the row: `GET /reports/schedules` returns each schedule's
 * recent runs, so opening this costs no request.
 *
 * Two things it is careful about:
 *
 *  - **A failed run shows its error.** Himaad: "Failed is the one I'd say I care
 *    about the most, because if a report dies at 10 minutes the person needs to
 *    know whether to retry or change a filter." So the error text is shown rather
 *    than a generic "failed".
 *  - **An expired result is not a link.** Scheduled results are retained by COUNT,
 *    and the results bucket also deletes every object by AGE (spec 152); linking to a
 *    swept result 410s. The row says "result expired" instead of offering a dead end.
 *
 * ## How many runs it shows (spec 165)
 *
 * Every run the list route sends, and the subtitle counts exactly those. The route used
 * to send ten whatever the schedule kept, so a keep-12 or keep-24 schedule read "last
 * 10 runs" while it held more — and the Queue hides a run whose result has aged out, so
 * runs 11 and later could be reached only by a direct link. cb-api now sends the
 * schedule's own "Keep results" count (at most 24) plus a margin of 3, so a run
 * retention keeps over the count is not cut off (`runListLimit`, `schedulesStore.js`).
 *
 * `runCount` is how many listable runs the schedule has. When it is more than the rows
 * here, the limit cut the list, and the subtitle says "latest N of M" rather than
 * letting N read as the whole history. An older worker sends no `runCount`; the
 * subtitle then states only what it shows.
 */

/*
 * Spec 152 — `result_expires_at` is the API's answer to "when does this run's stored
 * result stop being readable" (the earlier of `expires_at` and the results bucket's
 * age). A schedule's run carries `expires_at = null`, so the old test never fired for
 * one: every run older than the bucket's ~7 days on QA offered "Open" into a 410.
 * An older worker sends no `result_expires_at`, and the row's own `expires_at` decides.
 */
const isExpired = (r, now) => {
  const at = r.result_expires_at ?? r.expires_at
  return at != null && at <= now
}

/*
 * Spec 165 — the subtitle's count. `shown` is the rows this dialog renders, never a
 * constant; `runCount` (cb-api) adds "of M" only when the list was cut. Not exported:
 * the component file keeps `react-refresh/only-export-components` clean.
 */
const runsSubtitle = (name, shown, runCount) => {
  const total = Number(runCount)
  if (runCount != null && Number.isInteger(total) && total > shown) {
    return `${name} · latest ${shown} of ${total} runs`
  }
  return `${name} · last ${shown} run${shown === 1 ? '' : 's'}`
}

const ScheduleRunHistory = ({ schedule, onClose, now: nowProp }) => {
  // A lazy useState initializer, not `Date.now()` in the signature: calling an
  // impure function during render is both a purity-lint error and a real hazard —
  // the value would change on every re-render, so a row could flip to "expired"
  // mid-interaction. One reading, taken when the dialog opens, is what we want.
  const [nowAtOpen] = useState(() => Date.now())
  const now = nowProp ?? nowAtOpen
  const runs = schedule.runs || []
  // Spec 216: an unnamed Advanced Report schedule is "Advanced Report", never "Report #exago/…",
  // and its name (the firm's report name by default) is kept out of Pendo.
  const name = schedule.name || unnamedReportLabel(schedule.reportId)

  return (
    <Modal
      title="Run history"
      subtitle={<FirmText reportId={schedule.reportId}>{runsSubtitle(name, runs.length, schedule.runCount)}</FirmText>}
      onClose={onClose}
      maxWidth="max-w-2xl"
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" onClick={onClose} className="btn-cb-toolbar">Close</button>
        </>
      )}
    >
      {runs.length === 0 ? (
        <div className="px-5 py-10 text-center text-[12.8px] text-gray-500">
          This schedule hasn&apos;t run yet. Its first result will appear here.
        </div>
      ) : (
        <table className="w-full" role="table">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left">
              <th scope="col" className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">Ran</th>
              <th scope="col" className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">Outcome</th>
              <th scope="col" className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">Rows</th>
              <th scope="col" className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">Took</th>
              <th scope="col" className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">Result</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const failed = r.state === 'failed' || r.state === 'cancelled'
              const expired = isExpired(r, now)
              return (
                <tr key={r.id} className="border-b border-gray-100 align-top last:border-b-0">
                  <td className="px-4 py-2.5 text-[12.3px] text-gray-700">
                    {fmtWhenInZone(r.created_at, schedule.timezone)}
                  </td>
                  <td className="px-4 py-2.5 text-[12.3px]">
                    <span className={failed ? 'font-medium text-red-700' : 'text-gray-700'}>
                      {/* Spec 182: the words the Last-run cell uses too (`scheduleFormat`). */}
                      {RUN_STATE_LABEL[r.state] || r.state}
                    </span>
                    {/* Spec 216: an Advanced Report run's error can quote a prompt label the firm wrote. */}
                    {failed && r.error && (
                      <div className={`mt-0.5 max-w-[22rem] text-[11.5px] leading-snug text-gray-500 ${firmTextClass(schedule.reportId)}`.trim()}>{r.error}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[12.3px] text-gray-700">{fmtRows(r.row_count)}</td>
                  <td className="px-4 py-2.5 text-[12.3px] text-gray-700">
                    {r.duration_ms == null ? '—' : fmtDuration(r.duration_ms)}
                  </td>
                  <td className="px-4 py-2.5 text-[12.3px]">
                    {r.state !== 'ready' ? (
                      /* gray-500, not gray-400 — spec 118 §D1. At the hexes tailwindcss
                         4.2.1 emits: gray-400 #99a1af is 2.602:1 on white, under SC
                         1.4.3's 4.5:1; gray-500 #6a7282 is 4.836:1. */
                      <span className="text-gray-500">—</span>
                    ) : expired ? (
                      // Swept — by the count, or by the bucket's age — so a link here
                      // would 410.
                      <span className="text-gray-500">Result expired</span>
                    ) : (
                      <Link to={`${QUEUE_PATH}?jobId=${r.id}`} className="text-cb-accent-text hover:underline">
                        Open
                      </Link>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </Modal>
  )
}

export default ScheduleRunHistory
